"use client"

import type { ProductGroupItem } from "@/app/(site)/_components/Organisms/ProductGrids/ProductGridContent"
import type {
    GridContentBlock,
    RowGroup
} from "@/app/(site)/_components/Organisms/ProductGrids/types/grid-content"
import {
    getEagerCounts,
    getGridContentForPage,
    groupProductsWithModules
} from "@/app/(site)/_components/Organisms/ProductGrids/types/grid-content"
import { useMemo } from "react"

type UseGridContentLayoutParams = {
    visiblePageProducts: ProductGroupItem[][]
    products: ProductGroupItem[]
    activeStartPage: number
    hasFiltersOrSort: boolean
    gridContent: GridContentBlock[]
    desktopColumns: number
    mobileColumns: number
}

/** Computes desktop/mobile row groups and eager-load counts by merging grid content blocks across visible pages. */
export function useGridContentLayout({
    visiblePageProducts,
    products,
    activeStartPage,
    hasFiltersOrSort,
    gridContent,
    desktopColumns,
    mobileColumns
}: UseGridContentLayoutParams) {
    const productsCacheKey = products
        .map((product, index) => `${product.key}:${index}`)
        .join(",")
    const visiblePagesCacheKey = visiblePageProducts
        .map(page => page.map((product, index) => `${product.key}:${index}`).join(","))
        .join("|")

    const allPageBlocks = useMemo(() => {
        if (hasFiltersOrSort) return []

        const allBlocks: GridContentBlock[] = []
        let desktopOffset = 0
        let mobileOffset = 0

        /** Appends offset-adjusted content blocks for a single page and advances the row offsets. */
        const addPageBlocks = (pageNum: number, pageProducts: ProductGroupItem[]) => {
            const raw = getGridContentForPage(gridContent, pageNum)

            if (raw.length > 0) {
                allBlocks.push(
                    ...raw.map(b => ({
                        ...b,
                        positionRowDesktop: b.positionRowDesktop + desktopOffset,
                        positionRowMobile: b.positionRowMobile + mobileOffset
                    }))
                )
                const desktopGroups = groupProductsWithModules(pageProducts, raw, {
                    productsPerRow: desktopColumns
                })
                const mobileGroups = groupProductsWithModules(pageProducts, raw, {
                    productsPerRow: mobileColumns
                })
                desktopOffset += desktopGroups.length
                mobileOffset += mobileGroups.length
            } else {
                desktopOffset += Math.ceil(pageProducts.length / desktopColumns)
                mobileOffset += Math.ceil(pageProducts.length / mobileColumns)
            }
        }

        visiblePageProducts.forEach((pageProducts, index) => {
            addPageBlocks(activeStartPage + index, pageProducts)
        })

        return allBlocks
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasFiltersOrSort,
        visiblePagesCacheKey,
        activeStartPage,
        desktopColumns,
        mobileColumns
    ])

    const allPagesBlocksCacheKey = allPageBlocks
        ? allPageBlocks
              .map(
                  b =>
                      `${b._key}:${b.positionRowDesktop}:${b.positionRowMobile}:${b.page}`
              )
              .join(",")
        : false

    const desktopRowGroups: RowGroup<ProductGroupItem>[] = useMemo(
        () =>
            allPageBlocks.length === 0
                ? []
                : groupProductsWithModules(products, allPageBlocks, {
                      productsPerRow: desktopColumns,
                      isMobile: false
                  }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [productsCacheKey, allPagesBlocksCacheKey, desktopColumns]
    )

    const mobileRowGroups: RowGroup<ProductGroupItem>[] = useMemo(
        () =>
            allPageBlocks.length === 0
                ? []
                : groupProductsWithModules(products, allPageBlocks, {
                      productsPerRow: mobileColumns,
                      isMobile: true
                  }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [productsCacheKey, allPagesBlocksCacheKey, mobileColumns]
    )

    const { desktopEagerCount, mobileEagerCount } = useMemo(
        () => getEagerCounts(allPageBlocks, desktopColumns, mobileColumns),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [allPagesBlocksCacheKey, desktopColumns, mobileColumns]
    )

    return {
        desktopRowGroups,
        mobileRowGroups,
        desktopEagerCount,
        mobileEagerCount
    }
}
